import React, { useState, useEffect, useContext, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { APIContext } from '../../../utils/api'
import { GlobalContext } from '../../../utils/globalState'
import { colors } from '../../../themes/colors'
import { getSubindustryById, getIndustryBySubIndustryId, getSubindustriesById} from '../../../utils/industries'
import Switch from 'react-switch'
import useLocalStorage from '../../../utils/useLocalStorage'
import SummaryBar from './SummaryBar'
import CheckBox from './CheckBox'
import ScoreCardViewTime from './ScoreCardViewTime'
import ScoreCardBestAssets from './ScoreCardBestAssets'
import userNormIcon from '../../../assets/images/icon-norm-user.svg'
import industryNormIcon from '../../../assets/images/icon-norm-industry.svg'
import competitorNormIcon from '../../../assets/images/icon-norm-competitors.svg'
import styles from './styles.module.css'

// component function
export default function ReportSummary(props) {

	const { video } = props
	const { code } = useParams()
	const [norms, setNorms] = useState({clarity:{user:0, industry:0, competitor: 0}, brand:{user:0, industry:0, competitor: 0}, comms:{user:0, industry:0, competitor: 0}})
	const [reportSettings, setReportSettings] = useLocalStorage('reportSummarySettings', { showUserNorms:true, showIndustryNorms:false, showCompetitorNorms:false })
	const [addToAverage, setAddToAverage] = useState(video.excludeFromNorms === 0)
	const [globalState, setGlobalState] = useContext(GlobalContext)	// eslint-disable-line no-unused-vars
	const [fullBrandImage, setFullBrandImage] = useState()
	const [fullCommsImage, setFullCommsImage] = useState()
	const [bestBrandAsset, setBestBrandAsset] = useState()
	const [competitorsDisabled, setCompetitorsDisabled] = useState(true)
	const [competitorBrands, setCompetitorBrands] = useState([])
	const [bestCommsAsset, setBestCommsAsset] = useState()
	const [bestBrandObject, setBestBrandObject] = useState()
	const [bestCommsObject, setBestCommsObject] = useState()
	const context = useContext(APIContext)
	const receivedBenchmarksRef = useRef(video.benchmarks)
	const selectCompetitorRef = useRef()
	const cueTypes = globalState.cueTypes
	const ownVideo = video?.user?.id === globalState.userData?.id
	const duration = (video?.frameCount-3)/video?.frameRate

	// find label for video industry code
	const industryLabel = getSubindustryById(video?.industrySubCode)?.label || ''

	// scores
	const clarityScore = Math.round(video?.scoreClarity)
	const brandScore = Math.round(video?.scoreCueTypeCategoryAttention[1])
	const commsScore = Math.round(video?.scoreCueTypeCategoryAttention[2])

	// get benchmarks for user, industry and competitors
	useEffect(() => {
		const industry = video?.industrySubCode ? '?analysisType=' + video?.analysisType + '&industrySubCode=[' + getSubindustriesById(getIndustryBySubIndustryId(parseInt(video.industrySubCode)).id).map(i => i.id) + ']' : '' // get all subindustries within the main industry for this analysis
		context.io.socket.get('/api/v1/video/benchmarks' + industry, (data, res) => {
			if (res.statusCode === 200) {
				receivedBenchmarksRef.current = data
				setCompetitorBrands([...new Set(data.competitorAnalyses.map(v => v.brand.name))])
				handleBenchmarks(selectCompetitorRef.current.value)
			} else if (res.statusCode === 403) { // forbidden on shared report - use data from video object
				setCompetitorBrands([...new Set(receivedBenchmarksRef.current.competitorAnalyses.map(v => v.brand.name))])
				handleBenchmarks(selectCompetitorRef.current.value)
			} else {
				// TODO: error handling
			}
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [context.io.socket, reportSettings, addToAverage])

	// handle received bendhmark data
	function handleBenchmarks(brand) {
		const data = receivedBenchmarksRef.current
		const competitors = calculateAverages(data.competitorAnalyses, (brand ? [v => v.brand.name === brand] : []))
		const clarityNorms = {
			user: reportSettings.showUserNorms && data.user.clarity,
			industry: reportSettings.showIndustryNorms && data.industry.clarity,
			competitor: reportSettings.showCompetitorNorms && competitors.clarity
		}
		const brandNorms = {
			user: reportSettings.showUserNorms && data.user.attentionBrand,
			industry: reportSettings.showIndustryNorms && data.industry.attentionBrand,
			competitor: reportSettings.showCompetitorNorms && competitors.attentionBrand
		}
		const commsNorms = {
			user: reportSettings.showUserNorms && data.user.attentionCommunication,
			industry: reportSettings.showIndustryNorms && data.industry.attentionCommunication,
			competitor: reportSettings.showCompetitorNorms && competitors.attentionCommunication
		}
		data.competitorAnalyses.length > 0 && setCompetitorsDisabled(false)
		setNorms({clarity:clarityNorms, brand:brandNorms, comms:commsNorms})
	}

	// calculate attention and clarity averages for videos filtered by various criteria
	const calculateAverages = (data, filterFns) => {
		// Map, filter & single reduce to calculate sums and counts
		let workData = data.map(item => item)
		filterFns.forEach(filterFn => {
			workData = workData.filter(filterFn)
		})
		const { sums, counts } = workData.reduce((acc, item) => {
			const value0 = item.scoreCueTypeCategoryAttention['1']
			const value1 = item.scoreCueTypeCategoryAttention['2']
			const value2 = item.scoreClarity;
			if (typeof value0 === 'number') {
				acc.sums[0] += value0
				acc.counts[0] += 1
			}
			if (typeof value1 === 'number') {
				acc.sums[1] += value1
				acc.counts[1] += 1
			}
			if (typeof value2 === 'number') {
				acc.sums[2] += value2
				acc.counts[2] += 1
			}
			return acc
		},
			{ sums: [0, 0, 0], counts: [0, 0, 0] }
		)
		// Calculate averages
		return {
			attentionBrand: counts[0] > 0 ? sums[0] / counts[0] : 0,
			attentionCommunication: counts[1] > 0 ? sums[1] / counts[1] : 0,
			clarity: counts[2] > 0 ? sums[2] / counts[2] : 0
		}
	}

	// get strongest assets & images
	useEffect(() => {
		// get tagged segments
		const taggedBrandSegments = video?.segments.filter(s => s.cueType !== null && cueTypes.find(cueType => cueType.id === s.cueType)?.cueTypeCategory === 1)
		const taggedCommsSegments = video?.segments.filter(s => s.cueType !== null && cueTypes.find(cueType => cueType.id === s.cueType)?.cueTypeCategory === 2)

		// get strongest brand assets
		if (taggedBrandSegments.length > 0) {
			const bestBrand = taggedBrandSegments.reduce((a, b) => (a.scoreAttention > b.scoreAttention) ? a : b)
			setBestBrandAsset(bestBrand)
			getBestImage(bestBrand, 'brand')
		} else {
			setBestBrandAsset({scoreAttention: -1})
		}
		// get strongest comms assets
		if (taggedCommsSegments.length > 0) {
			const bestComms = taggedCommsSegments.reduce((a, b) => (a.scoreAttention > b.scoreAttention) ? a : b)
			setBestCommsAsset(bestComms)
			getBestImage(bestComms, 'comms')
		} else {
			setBestCommsAsset({scoreAttention: -1})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// calculate score at view-time durations
	function viewTimeScores(secs) {
		const lastFrame = Math.round(video?.frameRate * secs)
		return {
			clarity: Math.round(video?.frames[lastFrame].scoreClarity),
			brand: Math.round(video?.frames[lastFrame].scoreCueTypeCategoryAttention[1]),
			comms: Math.round(video?.frames[lastFrame].scoreCueTypeCategoryAttention[2])
		}
	}

	// toggle add to average
	function toggleAddToAverage() {
		// send excludeFromNorms setting to video endpoint
		const updateRequest = {
			excludeFromNorms: addToAverage
		}
		context.io.socket.post('/api/v1/video/' + video?.guid + '?dont_calculate', updateRequest, (data, res) => {
			if (res.statusCode === 200) {
				setAddToAverage(!addToAverage)
			} else {
				// TODO: error handling
			}
		})
	}

	// toggle show user norms
	function toggleShowUserNorms() {
		setReportSettings({...reportSettings, showUserNorms:!reportSettings.showUserNorms})
	}

	// toggle show industry norms
	function toggleShowIndustryNorms() {
		setReportSettings({...reportSettings, showIndustryNorms:!reportSettings.showIndustryNorms})
	}

	// toggle show competitor norms
	function toggleShowCompetitorNorms() {
		setReportSettings({...reportSettings, showCompetitorNorms:!reportSettings.showCompetitorNorms})
	}

	// competitor selected
	function competitorSelected(sel) {
		// adapt competitor select menu width to fit selected text
		const measure = document.getElementById('measureWidth')
		measure.innerHTML = sel.options[sel.selectedIndex].text
		sel.style.width = measure.offsetWidth + 30 + 'px'
		handleBenchmarks(sel.value)
	}
	
	// get chunk with image of best brand asset
	function getBestImage(asset, type) {
		if (!asset?.id) return
		const assetBestObject = video?.objects.filter(obj => obj.segment === asset.id).sort((a,b) => b?.attentionAbsCueTypeWeighted - a?.attentionAbsCueTypeWeighted)[0]
		const assetStartFrame = assetBestObject?.frameNo
		const chunk = Math.floor(assetStartFrame/30)
		const col = assetStartFrame % 5
		const row = Math.floor(assetStartFrame % 30 / 5)
		const image = new Image()
		image.crossOrigin = 'Anonymous'
		image.src = process.env.REACT_APP_GCS_BUCKET_URL + '/' + video?.guid + '/maps-frames/' + video?.guid + '-chunk' + ("0000000" + chunk).slice(-7) + '.jpg'
		image.onload = e=>cropImage(image, col, row, type)
		type === 'brand' && setBestBrandObject(assetBestObject)
		type === 'comms' && setBestCommsObject(assetBestObject)
	}

	// crop image to bounds of first object in best segment
	function cropImage(image, col, row, type) {
		const cropCanvas = document.createElement('canvas')
		const ctx = cropCanvas.getContext('2d')
		ctx.imageSmoothingEnabled = false
		const w = image.width/5 // chunk frame width
		const h = image.height/6 // chunk frame height
		cropCanvas.width = w * window.devicePixelRatio
		cropCanvas.height = h * window.devicePixelRatio
		ctx.drawImage(image, col * w, row * h , w, h, 0, 0, w * window.devicePixelRatio, h * window.devicePixelRatio)
		type === 'brand' && setFullBrandImage(cropCanvas.toDataURL('image/jpeg'))
		type === 'comms' && setFullCommsImage(cropCanvas.toDataURL('image/jpeg'))
	}

	// subheading text based on still or video
	const subHeadingText = video?.analysisType === 3 ? 'Scores based on a minimum of 3 seconds ad attention' : 'Scores based on viewers watching the full ad'

	// toggle
	const averageToggle = (globalState.loggedIn && !code && ownVideo &&
		<div className={styles.switchContainer}>
			<div onClick={toggleAddToAverage}>Add to my {industryLabel} average</div>
			<Switch
				onChange={toggleAddToAverage}
				checked={addToAverage}
				offColor={colors.switchBackgroundOff}
				offHandleColor={colors.switchKnobOff}
				onColor={colors.switchBackgroundOn}
				onHandleColor={colors.switchKnobOn}
				uncheckedIcon={false}
				checkedIcon={false}
				height={18}
				width={30}
				handleDiameter={14}
				activeBoxShadow=''
				id='add-to-industry-switch'
			/>
		</div>
	)

	// competitor select
	const competitorMenu = (
		<div className={styles.selectWrapper}>
			<select ref={selectCompetitorRef} className={styles.select} onClick={e=>e.stopPropagation()} onChange={e=>competitorSelected(e.target)} title="Select industry competitor">
				<option value=''>All competitors</option>
				{competitorBrands.map((brand,i) => {return <option key={'brand-'+i} value={brand}>{brand}</option>})}
			</select>
			<div id='measureWidth' className={styles.selectWidth} />
		</div>
	)

	// view-time score cards (if video)
	const viewTimeScoreCards = (video?.analysisType !== 3 &&
		<>
			<h4 className={styles.cardGroupHeading}>Ad strength at different view-time durations</h4>
			<div className={styles.scoreCardsWrapper}>
				{duration >= 2 && <ScoreCardViewTime title='2 seconds' scores={viewTimeScores(2)} />}
				{duration >= 6 && <ScoreCardViewTime title='6 seconds' scores={viewTimeScores(6)} />}
				{duration >= 15 && <ScoreCardViewTime title='15 seconds' scores={viewTimeScores(15)} />}
				<ScoreCardViewTime title='Full ad watched' scores={{clarity:clarityScore, brand:brandScore, comms:commsScore}} />
			</div>
		</>
	)
	
	// score cards for strongest assets (if we have any segments with a tagged object)
	const bestPerformingAssets = (bestBrandObject?.x !== undefined || bestCommsObject?.x !== undefined) && (
		<>
			<h4 className={styles.cardGroupHeading}>Strongest asset{bestBrandObject?.x === undefined || bestCommsObject?.x === undefined ? '' : 's'}</h4>
			<div className={styles.scoreCardsWrapper + ' ' + styles.scoreCardsBest}>
				{bestBrandObject?.x !== undefined && <ScoreCardBestAssets type='brand' scores={{brand: bestBrandAsset?.scoreAttention}} label={bestBrandAsset?.name} fullImage={fullBrandImage} bestObject={bestBrandObject} video={video} />}
				{bestCommsObject?.x !== undefined && <ScoreCardBestAssets type='comms' scores={{comms: bestCommsAsset?.scoreAttention}} label={bestCommsAsset?.name} fullImage={fullCommsImage} bestObject={bestCommsObject} video={video} />}
			</div>
		</>
	)

	return (
		<div className={styles.background}>
			<div className={styles.header}>
				<div className={styles.heading}>
					Ad strength summary
					<div className={styles.subHeading}>{subHeadingText}</div>
				</div>
				<div className={styles.switchesContainer}>
					{averageToggle}
				</div>
			</div>
			<div className={styles.barsWrapper}>
				<SummaryBar type={'clarity'} label={'Visual Clarity'} score={clarityScore} norms={norms.clarity} competitorsDisabled={competitorsDisabled} analysisType={video?.analysisType} />
				<SummaryBar type={'brand'} label={'Brand Attention'} score={brandScore} norms={norms.brand} competitorsDisabled={competitorsDisabled} analysisType={video?.analysisType} />
				<SummaryBar type={'comms'} label={'Comm. Attention'} score={commsScore} norms={norms.comms} competitorsDisabled={competitorsDisabled} analysisType={video?.analysisType} />
			</div>
			<div className={styles.normControlsWrapper}>
				<div className={styles.normControl} onClick={toggleShowUserNorms} title={ownVideo ? 'Your ' + industryLabel + ' average' : 'User’s ' + industryLabel + ' average'}>
					<CheckBox checked={reportSettings.showUserNorms} />
					{ownVideo ? 'My average' : 'User’s average'}
					<img src={userNormIcon} alt="" />
				</div>
				{(video?.analysisType === 1/* || video?.analysisType === 3*/) && // only normal videos and images at the moment. TODO: add animatics analysisTypes
					<div className={styles.normControl} onClick={toggleShowIndustryNorms} title={industryLabel + ' - industry average'}>
						<CheckBox checked={reportSettings.showIndustryNorms} />
						Industry average
						<img src={industryNormIcon} alt="" />
					</div>
				}
				<div title={competitorsDisabled ? 'No competitor ' + (video?.analysisType === 1 ? 'video' : video.analysisType === 2 ? 'animatic' : 'image') + ' analyses availabe for the ' + industryLabel + ' industry' : ''}>
					<div className={styles.normControl + ' ' + (competitorsDisabled ? styles.competitorsDisabled : '')} onClick={toggleShowCompetitorNorms} title={industryLabel + ' - competitor average'}>
						<CheckBox checked={reportSettings.showCompetitorNorms} />
						{competitorMenu} average
						<img src={competitorNormIcon} alt="" />
					</div>
				</div>
			</div>
			{viewTimeScoreCards}
			{bestPerformingAssets}
		</div>
	)
}
